import { useStore } from 'effector-react'
import { ParseKeys } from 'i18next'
import { useEffect, useState } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { inspectionFormModel } from '~/entities/Inspection'
import { $savingPhotosCount, savePhoto } from '~/entities/Inspection/model'
import {
  InspectionPhotoTypeEnum,
  InspectionReasonCodeEnum,
} from '~/shared/config/enums'
import { snackbarEnqueued } from '~/shared/lib/notifications'
import { Button } from '~/shared/ui/Buttons'
import { Camera, Checkbox, FormProvider, Switch } from '~/shared/ui/form'
import { Skeleton } from '~/shared/ui/SkeletonList'
import { FormHeader } from '../FormHeader'
import {
  TariffsPhotosErrorMessage,
  Wrapper,
  CheckBoxListWrapper,
} from './styled'
import { CarCabinInspectionFormValues } from './types'

const peculiarities: { id: string; label: ParseKeys }[] = [
  { id: 'hasTobaccoSmell', label: 'peculiarities.tobacco smell' },
  { id: 'isInteriorDirty', label: 'peculiarities.dirty interior' },
  { id: 'isDryCleaningNeed', label: 'peculiarities.dry cleaning required' },
]

type CarBodyInspectionFormProps = {
  onSuccess: () => void
  form: UseFormReturn<CarCabinInspectionFormValues>
  reasonCode: InspectionReasonCodeEnum
}

export function CarCabinInspectionForm({
  onSuccess,
  form,
  reasonCode,
}: CarBodyInspectionFormProps) {
  const { t } = useTranslation()
  const savingPhotoCount =
    useStore($savingPhotosCount)[InspectionPhotoTypeEnum.TARIFFS]
  const isPhotoPending = savingPhotoCount > 0

  const isTariffsPhotosRequired =
    reasonCode === InspectionReasonCodeEnum.PLAN ||
    reasonCode === InspectionReasonCodeEnum.RENTAL

  const carEquipmentsInteriorOptions = useStore(
    inspectionFormModel.$carEquipmentsInteriorOptions,
  )
  const isCarEquipmentsPending = useStore(
    inspectionFormModel.requestCarEquipmentsFx.pending,
  )

  const [tariffsPhotosErrorMessage, setTariffsPhotosErrorMessage] =
    useState<ParseKeys>()

  const tariffsPhotos = form.watch('tariffsPhotos')

  useEffect(() => {
    if (tariffsPhotos?.length) {
      setTariffsPhotosErrorMessage(undefined)
    }
  }, [tariffsPhotos])

  const handleOnSuccess = () => {
    if (!tariffsPhotos?.length && isTariffsPhotosRequired) {
      setTariffsPhotosErrorMessage('validate.upload a photo of the tariff grid')
      snackbarEnqueued({
        messageI18n:
          'alert.to proceed to the next step, upload a photo of the tariff grid',
        variant: 'warning',
      })
      return
    }
    onSuccess()
  }

  return (
    <FormProvider form={form} onSuccess={handleOnSuccess}>
      <Wrapper>
        <Camera
          buttonTitle='form.tariff grid photo'
          name='tariffsPhotos'
          saveFileFn={(file) =>
            savePhoto({
              file,
              type: InspectionPhotoTypeEnum.TARIFFS,
              form: form,
              fieldName: 'tariffsPhotos',
            })
          }
          isLoading={isPhotoPending}
        />
        {tariffsPhotosErrorMessage && (
          <TariffsPhotosErrorMessage data-testid='tariffs-photos-error-message'>
            {t(tariffsPhotosErrorMessage)}
          </TariffsPhotosErrorMessage>
        )}

        <FormHeader title='form.completeness' />

        {isCarEquipmentsPending && <Skeleton animated $height={150} />}

        <CheckBoxListWrapper>
          {!isCarEquipmentsPending &&
            carEquipmentsInteriorOptions.map((param) => (
              <Checkbox
                key={param.id}
                label={param.label}
                name={`equipmentsInterior.${param.id}`}
              />
            ))}
        </CheckBoxListWrapper>

        <FormHeader title='form.features' />

        {peculiarities.map((param) => (
          <Switch key={param.id} labelI18n={param.label} name={param.id} />
        ))}

        <Button
          fullWidth
          type='submit'
          variant='limeFlooded'
          uppercase
          style={{ marginTop: '16px' }}
        >
          {t('button.inspect trunk')}
        </Button>
      </Wrapper>
    </FormProvider>
  )
}
